import type { VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../lib/utils';
import type { CardVariant } from './variants';
import { cardHeaderVariants, cardVariants } from './variants';

/**
 * Lets `CardHeader` tint itself to match the `Card` it sits in, so the header
 * background stays in sync with the card border without every call site
 * repeating the variant. An explicit `variant` on the header still wins.
 */
const CardVariantContext = React.createContext<CardVariant>('neutral');

function Card({
  className,
  variant = 'neutral',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof cardVariants>) {
  return (
    <CardVariantContext.Provider value={variant ?? 'neutral'}>
      <div
        data-slot={'card'}
        className={cn(cardVariants({ variant }), className)}
        {...props}
      />
    </CardVariantContext.Provider>
  );
}

function CardHeader({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof cardHeaderVariants>) {
  const inheritedVariant = React.useContext(CardVariantContext);

  return (
    <div
      data-slot={'card-header'}
      className={cn(
        cardHeaderVariants({ variant: variant ?? inheritedVariant }),
        className
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot={'card-title'}
      className={cn('text-base font-medium', className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot={'card-description'}
      className={cn('text-neutral-muted-foreground text-sm', className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot={'card-action'}
      className={cn('col-start-2 self-start justify-self-end', className)}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot={'card-content'}
      className={cn('p-4 text-base', className)}
      {...props}
    />
  );
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot={'card-footer'}
      className={cn(
        'grid grid-cols-[auto_1fr] items-center gap-2 [&:has([data-slot=switch])]:grid-cols-[auto_1fr_auto] text-base px-4 pb-4',
        className
      )}
      {...props}
    />
  );
}

export {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
};
